export interface ReassignSharedTestParams {
    originalTestId: string;
    // Resolves the new test id.
    createCopy: () => Promise<string>;
    // null when the reassignment intentionally drops sharing.
    shareCopy: ((newTestId: string) => Promise<unknown>) | null;
    // Deleting a test row cascades its share record on the BE.
    deleteTest: (testId: string) => Promise<unknown>;
    captureError: (error: unknown, extra: Record<string, unknown>) => void;
}

/**
 * Copy + share + delete transaction of a shared-test reassignment; resolves the new test id or null.
 * Refreshes and analytics stay outside: a failing refresh must never trigger the rollback.
 * The rollback may only delete the copy while the original still exists, which holds because deleting
 * the original is the last awaited step — nothing may be added after it inside the try.
 */
export const reassignSharedTest = async (params: ReassignSharedTestParams): Promise<string | null> => {
    let newTestId: string | null = null;
    try {
        newTestId = await params.createCopy();
        if (params.shareCopy) {
            await params.shareCopy(newTestId);
        }
        await params.deleteTest(params.originalTestId);
        return newTestId;
    } catch (e) {
        params.captureError(e, { originalTestId: params.originalTestId, newTestId });
        if (newTestId) {
            await params.deleteTest(newTestId).catch((rollbackError) => {
                params.captureError(rollbackError, {
                    originalTestId: params.originalTestId,
                    newTestId,
                    rollback: true,
                });
            });
        }
        return null;
    }
};
